import React, { useState } from "react";
import { Mutation, Query } from "react-apollo";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import queries from "../../../queries";
import { backEndUri } from "../../../config";
import { agenda2Themes } from "@treeosk/common";
import ImageUploader from "../../uploaders/ImageUploader";
import PaletteColorPicker from "../../PaletteColorPicker";
import EditorSection from "./EditorSection";
import Products2ItemsEditor from "./Products2ItemsEditor";

// Editor of the "Produits 2.0" cell (cell_type 'products2'): an alphabetical list of items (speakers,
// exhibitors...) grouped in tabs by category, imported from a CSV or edited by hand.

const COLOR_GROUPS = [
  {
    id: 'module',
    title: 'Module',
    fields: [
      ['background_color', 'Fond du module'],
      ['text_color', 'Couleur du texte'],
    ],
  },
  {
    id: 'filter',
    title: 'Onglets (A, B-C...)',
    fields: [
      ['background_color_filter', "Fond de la barre d'onglets"],
      ['background_color_filter_checked', "Fond de l'onglet sélectionné"],
      ['text_color_filter', 'Texte des onglets'],
      ['text_color_filter_selected', "Texte de l'onglet sélectionné"],
      ['border_color_selected_filter', "Bordure de l'onglet sélectionné"],
    ],
  },
  {
    id: 'card',
    title: 'Cartes',
    fields: [
      ['background_color_card', 'Fond des cartes'],
      ['text_color_name', 'Couleur du nom'],
      ['text_color_subtitle', 'Couleur du sous-titre'],
      ['border_color_card', 'Bordure des cartes'],
    ],
  },
  {
    id: 'popup',
    title: 'Popup',
    fields: [
      ['background_color_pop_up', 'Fond du popup'],
      ['text_color_pop_up', 'Couleur du texte'],
      ['text_color_pop_up_title', 'Couleur du titre'],
      ['color_button_close', 'Couleur du bouton de fermeture'],
    ],
  },
];

const SIZE_FIELDS = [
  ['font_size_filter', 'Taille des onglets (em)'],
  ['font_size_name', 'Taille du nom (em)'],
  ['font_size_subtitle', 'Taille du sous-titre (em)'],
  ['image_size', 'Taille de la photo (em)'],
  ['font_size_popup', 'Taille du texte du popup (em)'],
];

const NUMBER_FIELDS = SIZE_FIELDS.map(([key]) => key);
const COLOR_KEYS = _.flatMap(COLOR_GROUPS, (group) => group.fields.map(([key]) => key));
const ALL_KEYS = [
  ...COLOR_KEYS, ...NUMBER_FIELDS,
  'bolder_radius_filter', 'show_all_tab', 'list_max_height_vh', 'font_family',
  'color_theme', 'background_image_uri', 'background_fit',
];

const BACKGROUND_FITS = [
  ['cover', "Remplir la cell (l'image peut être recadrée)"],
  ['contain', "Image entière (des bords peuvent rester vides)"],
  ['stretch', 'Étirer à la taille de la cell'],
];

const COLOR_THEMES = [
  ['custom', 'Personnalisé'],
  ['euroseeds', 'Euroseeds (couleurs et fond)'],
];

// Blanc, noir, vert et violet Euroseeds (le même violet que dans les thèmes de agenda2Themes.js)
const COLOR_PALETTE = ['#FFFFFF', '#000000', '#A6C82A', '#654455'];

const label = (key, defaultMessage) => (
  <FormattedMessage id={`products2.edit.${key}`} defaultMessage={defaultMessage} />
);

const toRgba = ({ rgb }) => `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;

// CSV upload with an explicit result (how many items, which lines or images were a problem).
function CsvImport({ cellId, onDone }) {
  const [state, setState] = useState({ busy: false, result: null, error: '' });

  const send = async (file) => {
    setState({ busy: true, result: null, error: '' });
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('filename', file.name);
      data.append('cellId', cellId);
      const response = await fetch(`${backEndUri}/csv_products2/`, { method: 'POST', body: data });
      const json = await response.json();
      if (!response.ok) {
        setState({ busy: false, result: null, error: json.message || `Erreur ${response.status}` });
        return;
      }
      setState({ busy: false, result: json, error: '' });
      await onDone();
    } catch (error) {
      setState({ busy: false, result: null, error: error.message });
    }
  };

  const { busy, result, error } = state;
  return (
    <div>
      <input
        type="file" accept=".csv,text/csv" disabled={busy}
        onChange={(e) => { const file = e.target.files[0]; if (file) send(file); e.target.value = ''; }}
      />
      {busy && <p>Import en cours… (les images sont téléchargées, ça peut prendre un moment)</p>}
      {error && <p style={{ color: '#b00020' }}>{error}</p>}
      {result &&
        <div>
          <p>✔ {result.message}</p>
          {result.skipped && result.skipped.length > 0 &&
            <p style={{ color: '#b26a00' }}>Lignes ignorées (sans nom) : {result.skipped.map((s) => s.line).join(', ')}.</p>
          }
          {result.imageErrors && result.imageErrors.length > 0 &&
            <div style={{ color: '#b26a00' }}>
              <p style={{ margin: '0 0 4px' }}>{result.imageErrors.length} image(s) n'ont pas pu être téléchargées (l'élément est créé sans photo) :</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {result.imageErrors.map((e) => <li key={e.url}>{e.url} — {e.reason}</li>)}
              </ul>
            </div>
          }
        </div>
      }
    </div>
  );
}

function Products2CellEditor({ cell }) {
  if (!cell || !cell.id) {
    return null;
  }

  return (
    <Query query={queries.cells.products2.get} variables={{ id: cell.id }}>
      {({ loading, error, data, refetch }) => {
        if (loading) return null;
        if (error) return `Error!: ${error}`;

        const { products2Cell } = data;
        if (!products2Cell) return null;

        return (
          <Mutation
            mutation={queries.cells.products2.update}
            refetchQueries={() => [{ query: queries.cells.products2.get, variables: { id: cell.id } }]}
          >
            {(updateCell) => (
              <Products2CellEditorForm cell={products2Cell} updateCell={updateCell} refetch={refetch} />
            )}
          </Mutation>
        );
      }}
    </Query>
  );
}

function Products2CellEditorForm({ cell, updateCell, refetch }) {
  const [values, setValues] = useState(() => _.pick(cell, ALL_KEYS));
  const set = (key, value) => setValues((previous) => ({ ...previous, [key]: value }));
  // The Euroseeds theme fills in its colors (still editable afterwards) and turns its background image on.
  const setTheme = (theme) => setValues((previous) => ({
    ...previous,
    ...(theme === 'euroseeds' ? agenda2Themes.EUROSEEDS_COLORS_PRODUCTS2 : {}),
    color_theme: theme,
  }));

  const save = () => {
    const input = { id: cell.id, ...values };
    NUMBER_FIELDS.forEach((key) => { input[key] = parseFloat(values[key]); });
    input.list_max_height_vh = parseInt(values.list_max_height_vh, 10);
    updateCell({ variables: { input } });
  };

  return (
    <div className="tsk-editor-form">
      <EditorSection title={<FormattedMessage id="products2.edit.section.import" defaultMessage="Import CSV" />}>
        <p style={{ margin: '0 0 8px' }}>
          Colonnes : <code>name</code> (obligatoire), <code>category</code> (l'onglet, ex. A ou B-C),
          {' '}<code>subtitle</code> (fonction), <code>description</code> (texte du popup, HTML accepté) et
          {' '}<code>image_uri</code> (adresse directe d'une image, téléchargée à l'import). Séparateur virgule ou
          point-virgule. L'import remplace tous les éléments, mais un fichier sans aucun élément ne change rien.
        </p>
        <CsvImport cellId={cell.id} onDone={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="products2.edit.section.items" defaultMessage="Éléments (speakers, produits...)" />} defaultOpen={false}>
        <Products2ItemsEditor cell={cell} refetch={refetch} />
      </EditorSection>

      <EditorSection title={<FormattedMessage id="products2.edit.section.options" defaultMessage="Affichage" />}>
        <div className="tsk-input-line">
          <label>{label('font_family', 'Typographie')}</label>
          <input
            type="text"
            value={values.font_family || ''}
            placeholder="OpenSans-Regular, 'Open Sans', sans-serif"
            onChange={(e) => set('font_family', e.target.value)}
          />
        </div>
        <p style={{ margin: '0 0 8px', opacity: 0.7 }}>
          Open Sans par défaut. Pour changer, indique le nom d'une police du projet (Paramètres du projet &gt; Polices),
          par exemple <code>OpenSans-Light</code>, ou une police système comme <code>Arial</code>.
        </p>
        <div className="tsk-input-line">
          <label>{label('show_all_tab', 'Afficher un onglet « Tous »')}</label>
          <input type="checkbox" checked={!!values.show_all_tab} onChange={(e) => set('show_all_tab', e.target.checked)} />
        </div>
        <div className="tsk-input-line">
          <label>{label('bolder_radius_filter', 'Onglets arrondis')}</label>
          <input type="checkbox" checked={!!values.bolder_radius_filter} onChange={(e) => set('bolder_radius_filter', e.target.checked)} />
        </div>
        <div className="tsk-input-line">
          <label>{label('list_max_height_vh', "Hauteur maximale de la liste (% de la hauteur de l'écran)")}</label>
          <input type="number" min="10" max="100" value={values.list_max_height_vh} onChange={(e) => set('list_max_height_vh', e.target.value)} />
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="products2.edit.section.background" defaultMessage="Fond" />} defaultOpen={false}>
        <p style={{ margin: '0 0 8px' }}>
          Ajoute ta propre image de fond quand tu en as besoin. Elle remplace le fond du thème Euroseeds ; retire-la
          pour retrouver le fond du thème (ou aucun fond). La couleur de fond se règle dans Couleurs &gt; Module.
        </p>
        <div className="tsk-input-line tsk-input-line--full">
          <label>{label('background_image_uri', 'Image de fond')}</label>
          <ImageUploader
            value={values.background_image_uri}
            canDelete
            onUpload={(uri) => set('background_image_uri', uri)}
            onDelete={() => set('background_image_uri', null)}
          />
        </div>
        <div className="tsk-input-line">
          <label>{label('background_fit', "Ajustement de l'image")}</label>
          <select value={values.background_fit || 'cover'} onChange={(e) => set('background_fit', e.target.value)}>
            {BACKGROUND_FITS.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
      </EditorSection>

      <EditorSection title={<FormattedMessage id="products2.edit.section.colors" defaultMessage="Couleurs" />} defaultOpen={false}>
        <div className="tsk-input-line">
          <label>{label('color_theme', 'Thème de couleurs')}</label>
          <select value={values.color_theme || 'custom'} onChange={(e) => setTheme(e.target.value)}>
            {COLOR_THEMES.map(([value, text]) => (
              <option key={value} value={value}>{text}</option>
            ))}
          </select>
        </div>
        {COLOR_GROUPS.map((group) => (
          <EditorSection nested key={group.id} title={label(`group.${group.id}`, group.title)}>
            {group.fields.map(([key, text]) => (
              <div className="tsk-input-line" key={key}>
                <label>{label(key, text)}</label>
                <PaletteColorPicker
                  palette={COLOR_PALETTE}
                  allowNone
                  disableAlpha={false}
                  color={values[key]}
                  onChange={(color) => set(key, toRgba(color))}
                />
              </div>
            ))}
          </EditorSection>
        ))}
      </EditorSection>

      <EditorSection title={<FormattedMessage id="products2.edit.section.sizes" defaultMessage="Tailles" />} defaultOpen={false}>
        {SIZE_FIELDS.map(([key, text]) => (
          <div className="tsk-input-line" key={key}>
            <label>{label(key, text)}</label>
            <input type="number" step="0.1" min="0.3" value={values[key]} onChange={(e) => set(key, e.target.value)} />
          </div>
        ))}
      </EditorSection>

      <button className="tsk-button" onClick={save}>
        <FormattedMessage id={'common.button.submit'} />
      </button>
    </div>
  );
}

export default Products2CellEditor;
